import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { ReactNode } from 'react';

import { ProfileSettingVisibility, ProfileSettings } from '@flipster/apis/v1/user/types';
import { FormattedMessage } from '@flipster/i18n';
import { useOverlayItem } from '@flipster/overlay';
import { Modal } from '@flipster/react/design-system-legacy/modal';
import { Button } from '@flipster/react/design-system/button';
import { Dialog, dialog } from '@flipster/react/design-system/dialog';
import { Toggle } from '@flipster/react/design-system/toggle';
import CautionCircleIcon from '@flipster/react/icon/generated/CautionCircleIcon';
import { css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { map } from '@flipster/utils/map';
import segment from '@flipster/utils/segment';
import { ProfileEvent } from '@flipster/utils/segment/events/profile';

import { profileSettingBunja } from '../../../shared/community/profile';
import { profileBunja } from '../../bunja';
import { booleanToVisibility, visibilityToBoolean } from '../../misc';

interface PrivacySetting {
  title: ReactNode;
  description: ReactNode;
  type: keyof ProfileSettings;
  action: Record<ProfileSettingVisibility, { title: ReactNode; message: ReactNode }>;
}
const PrivacySettings: PrivacySetting[] = [
  {
    title: <FormattedMessage id='Autogen_common.r251126.s_Performance' />,
    description: (
      <FormattedMessage id='Autogen_common.r251126.s_Let_others_view_your_top_performing_positions_including_position_size_and_P_L' />
    ),
    type: 'performanceVisibility',
    action: {
      PUBLIC: {
        title: <FormattedMessage id='Autogen_common.r251126.s_Your_performance_is_now_public' />,
        message: (
          <FormattedMessage id='Autogen_common.r251126.s_Your_performance_including_position_size_and_P_L_are_now_visible_to_other_users' />
        ),
      },
      PRIVATE: {
        title: <FormattedMessage id='Autogen_common.r251126.s_Your_performance_will_be_set_to_private' />,
        message: <FormattedMessage id='Autogen_common.r251126.s_Performance_Private_Body' />,
      },
    },
  },
  {
    title: <FormattedMessage id='Autogen_common.r251126.s_Trading_activity' />,
    description: <FormattedMessage id='Autogen_common.r251126.s_Trading_Activity_Description' />,
    type: 'tradeHistoryVisibility',
    action: {
      PUBLIC: {
        title: <FormattedMessage id='Autogen_common.r251126.s_Your_trading_activity_is_now_public' />,
        message: (
          <FormattedMessage id='Autogen_common.r251126.s_Your_detailed_trading_activity_including_position_activities_is_now_visible_to_other_users' />
        ),
      },
      PRIVATE: {
        title: <FormattedMessage id='Autogen_common.r251126.s_Trading_Activity_Private_Title' />,
        message: <FormattedMessage id='Autogen_common.r251126.s_Trading_Activity_Private_Body' />,
      },
    },
  },
];

export const ProfileSettingsModal = () => {
  const { isOpen, close, unmount } = useOverlayItem();
  const t = useTranslations();
  const { profileAtom } = useBunja(profileBunja);
  const profile = useAtomValue(profileAtom);
  const { putUserProfileMutationAtom } = useBunja(profileSettingBunja);
  const { mutateAsync, isPending, variables } = useAtomValue(putUserProfileMutationAtom);

  return (
    <Modal isOpen={isOpen} onExit={unmount}>
      <Modal.Close onClose={close} />
      <Modal.Header>
        <Modal.Title label={t('Autogen_common.r251126.s_Profile_settings')} />
      </Modal.Header>
      <Modal.Content>
        <section className={stack({ gap: 16 })}>
          <div className={stack({ gap: 4 })}>
            <h3 className={css({ textStyle: 'Text/L/Strong', color: 'content.default.level-5' })}>
              <FormattedMessage id='Autogen_common.r251126.s_Privacy_Settings' />
            </h3>
            <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2' })}>
              <FormattedMessage id='Autogen_common.r251126.s_To_add_social_links_to_your_profile_both_performance_and_trading_activity_must_be_public' />
            </p>
          </div>
          <div className={stack({ borderRadius: 8, backgroundColor: 'background.default.level-3' })}>
            {PrivacySettings.map(({ title, description, type, action }, index) => {
              const pendingChecked = isPending && map(variables?.settings?.[type], visibilityToBoolean);
              const checked = (pendingChecked || map(profile?.mySettings[type], visibilityToBoolean)) ?? false;

              return (
                <div key={index} className={stack({ p: 16, gap: 8 })}>
                  <div className={hstack({ justify: 'space-between' })}>
                    <p className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-5' })}>{title}</p>
                    <Toggle
                      css={{ flexShrink: 0 }}
                      selected={checked}
                      onChange={async () => {
                        const next = !checked;
                        const nextVisibility = booleanToVisibility(next);
                        if (nextVisibility === 'PUBLIC') {
                          const { title, message } = action[nextVisibility];
                          await mutateAsync({
                            settings: { [type]: nextVisibility },
                          });
                          return dialog.open({
                            title: {
                              label: (
                                <p className={css({ textStyle: 'Title/S', color: 'content.default.level-5' })}>
                                  {title}
                                </p>
                              ),
                            },
                            content: message,
                            buttons: {
                              primary: (
                                <Dialog.Button
                                  hierarchy='primary'
                                  label={<FormattedMessage id='Common.r250401.Done' />}
                                />
                              ),
                            },
                          });
                        }
                        if (nextVisibility === 'PRIVATE') {
                          const { title, message } = action[nextVisibility];
                          return dialog.open({
                            title: {
                              label: (
                                <p
                                  className={hstack({
                                    textStyle: 'Title/S',
                                    color: 'content.default.level-5',
                                    gap: 6,
                                    alignItems: 'self-start',
                                    tabletDown: {
                                      '& > svg': {
                                        width: 20,
                                        height: 20,
                                        flexShrink: 0,
                                      },
                                    },
                                  })}
                                >
                                  <CautionCircleIcon color={token('colors.content.default.level-3')} />
                                  <span>{title}</span>
                                </p>
                              ),
                            },
                            content: message,
                            buttons: {
                              primary: (
                                <Dialog.Button
                                  hierarchy='primary'
                                  label={<FormattedMessage id='Autogen_common.r251126.s_Set_to_private' />}
                                  onClick={() => mutateAsync({ settings: { [type]: nextVisibility } })}
                                />
                              ),
                              secondary: (
                                <Dialog.Button
                                  hierarchy='secondary'
                                  color='neutral'
                                  label={<FormattedMessage id='Common.r250401.Close' />}
                                />
                              ),
                            },
                          });
                        }
                      }}
                    />
                  </div>
                  <p
                    className={css({
                      w: { base: '100%', tablet: 306 },
                      textStyle: 'Text/M/Regular',
                      color: 'content.default.level-2',
                    })}
                  >
                    {description}
                  </p>
                </div>
              );
            })}
          </div>
        </section>
      </Modal.Content>
      <Modal.Footer>
        <Button
          size='large'
          label={t('Autogen_common.r251126.s_Done')}
          onClick={() => {
            segment.track(ProfileEvent.ProfileSettingsDoneClick);
            close();
          }}
        />
      </Modal.Footer>
    </Modal>
  );
};
